"use client";

import type { ReactNode } from "react";
import type { PeriodKind } from "@/libs/tracking/dashboardWeeks";
import { activityLevelLabel } from "@/libs/tracking/activityLevel";
import type { WeekProfileSummaryData } from "@/libs/tracking/weekProfileSummary";
import { INSTAGRAM_LINK_PROPS, instagramProfileUrl } from "@/libs/instagramUrl";

function ActivityLevelText({
  level,
}: {
  level: WeekProfileSummaryData["activityLevel"];
}) {
  const label = activityLevelLabel(level);
  switch (level) {
    case "very active":
      return <span className="font-bold text-emerald-600">{label}</span>;
    case "moderately active":
      return <span className="font-bold text-amber-600">{label}</span>;
    case "barely active":
      return <span className="font-bold text-rose-deep">{label}</span>;
  }
}

interface ChangesSummaryCardProps {
  data?: WeekProfileSummaryData | null;
  kind: PeriodKind;
  nextReportLabel?: string;
  isScraping?: boolean;
  isCollecting?: boolean;
  weekLabel?: string;
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

const SUMMARY_TEXT = "text-[13.5px] leading-relaxed text-ink";

function IconBadge({ children }: { children: ReactNode }) {
  return (
    <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-rose-softer text-rose-deep">
      {children}
    </span>
  );
}

function SummaryItem({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <div className="flex items-center gap-3 pt-4 first:pt-0">
      <IconBadge>{icon}</IconBadge>
      <div className={`min-w-0 flex-1 ${SUMMARY_TEXT}`}>{children}</div>
    </div>
  );
}

function ActivityIcon({ className = "h-3.5 w-3.5" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  );
}

function UsersIcon({ className = "h-3.5 w-3.5" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function HeartIcon({ className = "h-3.5 w-3.5" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
}

function HashIcon({ className = "h-3.5 w-3.5" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="9" x2="20" y2="9" />
      <line x1="4" y1="15" x2="20" y2="15" />
      <line x1="10" y1="3" x2="8" y2="21" />
      <line x1="16" y1="3" x2="14" y2="21" />
    </svg>
  );
}

function FollowIcon({ className = "h-3.5 w-3.5" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <line x1="19" y1="8" x2="19" y2="14" />
      <line x1="22" y1="11" x2="16" y2="11" />
    </svg>
  );
}

function baselineIntro(data: WeekProfileSummaryData, nextReportLabel?: string): string {
  if (data.noPostsInLast7Days && data.usedPostFallback) {
    const postNote = `most recent ${plural(data.postsAnalyzed, "post", "posts")}`;
    const ageNote =
      data.daysSinceLastPost != null
        ? ` (most recent was ${data.daysSinceLastPost} ${plural(data.daysSinceLastPost, "day", "days")} ago)`
        : "";
    return `This is your starting snapshot. They had no posts in the last 7 days, so we analyzed likes on their ${postNote}${ageNote} to estimate engagement. Your first full weekly report arrives ${nextReportLabel ?? "soon"}.`;
  }

  return `This is your starting snapshot from the last 7 days to establish a baseline. Once we collect more data, upcoming reports will become more accurate. Your first full weekly report arrives ${nextReportLabel ?? "soon"}.`;
}

function hasFollowChanges(data: WeekProfileSummaryData): boolean {
  return (
    data.gainedFollowers > 0 ||
    data.lostFollowers > 0 ||
    data.gainedFollowing > 0 ||
    data.lostFollowing > 0
  );
}

export default function ChangesSummaryCard({
  data,
  kind,
  nextReportLabel,
  isScraping = false,
  isCollecting = false,
  weekLabel = "",
}: ChangesSummaryCardProps) {
  const favoriteHandle = data?.favoriteUser.username
    ? `@${data.favoriteUser.username}`
    : null;
  const isBaseline = kind === "baseline";
  const periodPhrase = isBaseline ? "your first 7 days" : "this period";
  const showEngagement = data?.hasEngagementData !== false && !isCollecting;

  return (
    <div className="mt-3 rounded-2xl border border-line bg-surface px-4 py-4 shadow-soft sm:px-5">
      {isCollecting && (
        <div className="mb-4 flex items-center gap-2.5 rounded-full border border-rose/25 bg-rose-softer/80 px-3.5 py-2">
          <span className="inline-block h-2 w-2 shrink-0 animate-pulse rounded-full bg-rose" />
          <p className="text-[12px] font-medium leading-snug text-rose-deep">
            Collecting data - your report will be ready in ~10–30 seconds
          </p>
        </div>
      )}

      {isBaseline && nextReportLabel && !isCollecting && data && (
        <div className="mb-4 rounded-xl border border-rose/20 bg-rose-softer/50 px-3.5 py-3">
          <p className="text-[13px] leading-relaxed text-slate-600">
            {baselineIntro(data, nextReportLabel)}
          </p>
        </div>
      )}

      <div className="flex gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <p className="text-[12px] font-bold uppercase tracking-wider text-muted">
                Latest Activity
              </p>
            </div>
            <span className="text-[11.5px] font-medium text-muted">
              {data?.weekLabel ?? weekLabel}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-4 space-y-0">
        {isCollecting && (
          <div className="space-y-3">
            <div className="sk h-14 w-full rounded-xl" />
            <div className="sk h-14 w-[92%] rounded-xl" />
            <div className="sk h-14 w-[85%] rounded-xl" />
          </div>
        )}

        {!isCollecting && isScraping && !showEngagement && (
          <p className={SUMMARY_TEXT}>
            Collecting post and engagement data… summary updates as scrape jobs finish.
          </p>
        )}

        {!isCollecting && showEngagement && data && (
          <SummaryItem icon={<ActivityIcon />}>
            <p>
              <span className="font-semibold">{data.handle}</span> is{" "}
              <ActivityLevelText level={data.activityLevel} />
              .{" "}
              {data.postsAnalyzed > 0 ? (
                <>
                  We analyzed{" "}
                  <span className="font-bold"></span>their latest {" "}
                  {plural(data.postsAnalyzed, "post", "posts")}
                  {data.noPostsInLast7Days && data.usedPostFallback
                    ? " (none posted in the last 7 days)"
                    : ""}
                  . Their posts received{" "}
                  <span className="font-bold">{data.mediaLikes}</span>{" "}
                  {plural(data.mediaLikes, "like", "likes")} from{" "}
                  <span className="font-bold">{data.likedUsersCount}</span>{" "}
                  {plural(data.likedUsersCount, "account", "accounts")}
                  {data.avgLikesPerPost > 0 ? (
                    <>
                      {" "}
                      - about{" "}
                      <span className="font-bold">{data.avgLikesPerPost}</span> likes per post
                    </>
                  ) : null}
                  .
                </>
              ) : (
                <>
                  Their posts received{" "}
                  <span className="font-bold">{data.mediaLikes}</span>{" "}
                  {plural(data.mediaLikes, "like", "likes")}.
                </>
              )}
            </p>
          </SummaryItem>
        )}

        {!isCollecting && showEngagement && data && data.likedUsersCount > 0 && (
          <SummaryItem icon={<UsersIcon />}>
            <p>
              During {periodPhrase},{" "}
              <span className="font-bold">{data.likedUsersCount}</span>{" "}
              {plural(data.likedUsersCount, "account", "accounts")} liked their posts. On
              average, each liker left{" "}
              <span className="font-bold">{data.avgLikesPerUser.toFixed(2)}</span>{" "}
              {plural(Math.round(data.avgLikesPerUser) || 1, "like", "likes")}.
            </p>
          </SummaryItem>
        )}

        {!isCollecting && showEngagement && data && favoriteHandle && data.favoriteUser.likes > 0 && (
          <SummaryItem icon={<HeartIcon />}>
            <p>
              Their top liker during {periodPhrase} is{" "}
              <a
                href={instagramProfileUrl(data.favoriteUser.username)}
                {...INSTAGRAM_LINK_PROPS}
                className="font-semibold hover:text-rose-deep"
              >
                {favoriteHandle}
              </a>
              , who left{" "}
              <span className="font-bold">{data.favoriteUser.likes}</span>{" "}
              {plural(data.favoriteUser.likes, "like", "likes")} on their posts.
            </p>
          </SummaryItem>
        )}

        {/*}
        {!isCollecting && showEngagement && data && data.hashtags.length > 0 && (
          <SummaryItem icon={<HashIcon />}>
            <p>
              Top hashtags on the analyzed posts:{" "}
              {data.hashtags.map((tag, i) => (
                <span key={tag}>
                  <span className="font-semibold">{tag}</span>
                  {i < data.hashtags.length - 1 ? ", " : "."}
                </span>
              ))}
            </p>
          </SummaryItem>
        )}
        */}

        {!isCollecting && showEngagement && data && hasFollowChanges(data) && (
          <SummaryItem icon={<FollowIcon />}>
            <p>
              Follower/following changes during {periodPhrase}:{" "}
              {data.gainedFollowers > 0 && (
                <span>
                  <span className="font-bold">+{data.gainedFollowers}</span>{" "}
                  {plural(data.gainedFollowers, "follower", "followers")}
                </span>
              )}
              {data.gainedFollowers > 0 && data.lostFollowers > 0 && ", "}
              {data.lostFollowers > 0 && (
                <span>
                  <span className="font-bold">-{data.lostFollowers}</span>{" "}
                  {plural(data.lostFollowers, "follower", "followers")}
                </span>
              )}
              {(data.gainedFollowers > 0 || data.lostFollowers > 0) &&
                (data.gainedFollowing > 0 || data.lostFollowing > 0) &&
                "; "}
              {data.gainedFollowing > 0 && (
                <span>
                  followed{" "}
                  <span className="font-bold">{data.gainedFollowing}</span>{" "}
                  {plural(data.gainedFollowing, "account", "accounts")}
                </span>
              )}
              {data.gainedFollowing > 0 && data.lostFollowing > 0 && ", "}
              {data.lostFollowing > 0 && (
                <span>
                  unfollowed{" "}
                  <span className="font-bold">{data.lostFollowing}</span>{" "}
                  {plural(data.lostFollowing, "account", "accounts")}
                </span>
              )}
              .
            </p>
          </SummaryItem>
        )}

        {!isCollecting && !showEngagement && !isScraping && data && (
          <p className={SUMMARY_TEXT}>
            No post engagement data for this period yet.
          </p>
        )}
      </div>
    </div>
  );
}
